/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';

import {
  Container,
  Infos,
  StyledFont,
  DashboardsCount,
  EmptyList,
  Title,
  DashboardText,
  TableOverFlow,
  Options,
  BatchActions,
} from './styled/DashboardLinks.styled';

import DataTable from '../../../juristec-ui/core/DataTable';
import TablePagination from '../../../juristec-ui/core/TablePagination';
import CopyText from '../../../juristec-ui/core/CopyText';
import Button from '../../../juristec-ui/core/Button';
import Tooltip from '../../../juristec-ui/core/Tooltip';
import IconButton from '../../../juristec-ui/core/IconButton';

import { Reload, Close, Info } from '../../../juristec-ui/icons';

const DashboardsLinks = ({
  dashboards,
  user,
  genPublicUrl,
  getDashInfo,
}) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [_dashboards, _setDashboards] = useState([]);

  useEffect(() => {
    if (dashboards) {
      _setDashboards(dashboards);
    }
  }, [dashboards]);

  const handleGen = async (dash, operation) => {
    const res = await genPublicUrl(dash, operation);
    if (!res.error) {
      _setDashboards((prev) => prev.map((d) => {
        if (d.id !== dash.id) {
          return d;
        }
        return {
          ...d,
          publicUrl: res.publicUrl ? `${window.location.origin}${res.publicUrl}` : '',
          publicTimestamp: res.localTimestamp,
        };
      }));
    }
  };

  const handleAllDashboards = async (operation) => {
    const dashboardsToUpdate = _dashboards.filter(
      (dash) => dash?.publicUrl && dash?.owner === user.id,
    );
    await Promise.all(
      dashboardsToUpdate.map((dash) => handleGen(dash, operation)),
    );
  };

  const DashOptions = ({ dash }) => {
    const handleUpdatePublicLink = () => {
      handleGen(dash, 'update');
    };

    const handleRevokePublicLink = () => {
      handleGen(dash, 'delete');
    };

    const handleDashboardInfo = () => {
      getDashInfo(dash);
    };

    return (
      <Options>
        {!(!dash?.publicUrl || dash?.owner !== user.id) && (
          <>
            <Tooltip text="Atualizar link público" atModal>
              <IconButton
                variant="pattern"
                color="primary"
                onClick={handleUpdatePublicLink}
                disabled={!dash?.publicUrl || dash?.owner !== user.id}
              >
                <Reload />
              </IconButton>
            </Tooltip>
            <Tooltip text="Revogar link público" atModal>
              <IconButton
                variant="pattern"
                color="primary"
                onClick={handleRevokePublicLink}
                disabled={!dash?.publicUrl || dash?.owner !== user.id}
              >
                <Close />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip text="Outras informações" atModal>
          <IconButton
            variant="pattern"
            color="primary"
            onClick={handleDashboardInfo}
          >
            <Info />
          </IconButton>
        </Tooltip>
      </Options>
    );
  };
  DashOptions.propTypes = {
    dash: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      createdAt: PropTypes.string,
      owner: PropTypes.string,
      ownerName: PropTypes.string,
      publicUrl: PropTypes.string,
      publicTimestamp: PropTypes.string,
      authUrl: PropTypes.string,
    }),
  };

  const dashInfo = (dash) => (
    <DashboardText>
      <h4>{dash?.name || 'Sem Nome'}</h4>
      <div>
        <strong>Grupo:</strong>
        {' '}
        {dash?.group?.label ? dash.group.label : 'Sem grupo'}
      </div>
    </DashboardText>
  );

  const userInfo = (dash) => (
    <DashboardText>
      {dash.owner === user.id ? (
        <h4>Eu</h4>
      ) : (
        <h4>{dash.ownerName || 'Sem Nome'}</h4>
      )}
      <CopyText
        text={dash.email}
        textContainerStyle={
          {
            fontSize: '12px',
            fontWeight: 'normal',
            width: '200px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }
        }
      />
    </DashboardText>
  );

  const columns = [
    {
      field: 'name',
      label: 'Nome do Dashboard',
      sortable: true,
      valueGetter: (param) => dashInfo(param),
    },
    {
      field: 'ownerName',
      label: 'Proprietário',
      sortable: true,
      valueGetter: (param) => userInfo(param),
    },
    {
      field: 'publicUrl',
      label: 'Link Público',
      sortable: true,
      valueGetter: (param) => (
        param?.publicUrl ? (
          <CopyText
            text={param?.publicUrl}
            textContainerStyle={{
              fontSize: '12px',
              width: '200px',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          />
        ) : 'Link não criado'
      ),
    },
    {
      field: 'publicTimestamp',
      label: 'Última atualização',
      sortable: true,
      valueGetter: (param) => {
        if (param?.publicUrl) {
          return param?.publicTimestamp ? new Date(param.publicTimestamp).toLocaleString() : '-';
        }
        return '-';
      },
    },
    {
      field: 'options',
      role: 'optionsBtn',
      label: 'Opções',
      valueGetter: (param) => <DashOptions dash={param} />,
    },
  ];

  return (
    <Container>
      <Infos>
        <DashboardsCount>
          <StyledFont>
            <Title>Quantidade de Dashboards</Title>
            {' '}
            <span>
              {_dashboards.length}
            </span>
          </StyledFont>
        </DashboardsCount>
        <BatchActions>
          <Button variant="contained" onClick={() => handleAllDashboards('update')}>
            Atualizar todos os links
          </Button>
          <Button variant="outlined" onClick={() => handleAllDashboards('delete')}>
            Revogar todos os links
          </Button>
        </BatchActions>
      </Infos>
      {_dashboards.length > 0 ? (
        <>
          <TableOverFlow>
            <DataTable
              columns={columns}
              rowData={_dashboards}
              rowColor={theme.tableBackground}
              strippedRowsColor={theme.containerOdd}
              defaultSortField="name"
              defaultSortOrder="ascending"
              headerColor="transparent"
              // compactView
              theadStyle={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                backgroundColor: theme.background,
              }}
              tbodyStyle={{
                fontSize: '14px',
              }}
              usePagination
              page={page}
              itemsPerPage={8}
            />
          </TableOverFlow>
          <TablePagination
            page={page}
            setPage={setPage}
            totalPages={
              Math.floor(_dashboards.length % 8 === 0
                ? (_dashboards.length / 8) - 1
                : _dashboards.length / 8)
            }
          />
        </>
      ) : (
        <EmptyList>
          Você ainda não possui dashboards.
        </EmptyList>
      )}
    </Container>
  );
};

DashboardsLinks.propTypes = {
  dashboards: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
    createdAt: PropTypes.string,
    owner: PropTypes.string,
    ownerName: PropTypes.string,
    publicUrl: PropTypes.string,
    publicTimestamp: PropTypes.string,
    authUrl: PropTypes.string,
  })),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  genPublicUrl: PropTypes.func,
  getDashInfo: PropTypes.func,
};

export default DashboardsLinks;
